import React from 'react';
import { useTranslate } from '../../hooks/useTranslate';
import { TRAINING_MESSAGES } from '../../constants/training';
import type { TrainingVideo } from '../../types/training';

interface VideoDetailPanelProps {
    video: TrainingVideo | null;
    onPrevious: (() => void) | null;
    onNext: (() => void) | null;
}

function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

const VideoDetailPanel: React.FC<VideoDetailPanelProps> = ({ video, onPrevious, onNext }) => {
    const { t } = useTranslate();

    if (!video) {
        return (
            <aside className="flex flex-col h-full bg-white border-l border-gray-200">
                <div className="flex-1 flex items-center justify-center p-6">
                    <p className="text-gray-400 text-sm text-center">
                        {t(TRAINING_MESSAGES.SELECT_VIDEO)}
                    </p>
                </div>
            </aside>
        );
    }

    return (
        <aside className="flex flex-col h-full bg-white border-l border-gray-200">
            {/* Now Playing badge */}
            <div className="flex-shrink-0 px-5 pt-5 pb-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-[#D31145] text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-[#D31145] animate-pulse" />
                    {t(TRAINING_MESSAGES.NOW_PLAYING)}
                </span>
            </div>

            {/* Video metadata */}
            <div className="flex-1 overflow-y-auto px-5 pb-5" style={{ scrollbarWidth: 'thin' }}>
                <h2 className="text-lg font-bold text-gray-900 leading-snug mb-3">
                    {video.title}
                </h2>

                {/* Tags */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                        📂 {video.category}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                        ⏱ {formatDuration(video.durationSeconds)}
                    </span>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-100 mb-4" />

                {/* Description */}
                <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        {t(TRAINING_MESSAGES.VIDEO_DETAILS)}
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                        {video.description}
                    </p>
                </div>
            </div>

            {/* Navigation footer */}
            <div className="flex-shrink-0 border-t border-gray-100 px-5 py-3 flex items-center justify-between bg-gray-50/50">
                <button
                    type="button"
                    disabled={!onPrevious}
                    onClick={onPrevious ?? undefined}
                    className="inline-flex items-center gap-1 text-sm font-medium text-[#D31145] disabled:text-gray-300 disabled:cursor-not-allowed hover:underline transition-colors"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {t(TRAINING_MESSAGES.PREVIOUS)}
                </button>
                <button
                    type="button"
                    disabled={!onNext}
                    onClick={onNext ?? undefined}
                    className="inline-flex items-center gap-1 text-sm font-medium text-[#D31145] disabled:text-gray-300 disabled:cursor-not-allowed hover:underline transition-colors"
                >
                    {t(TRAINING_MESSAGES.NEXT)}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </aside>
    );
};

export default VideoDetailPanel;
